import Scrollbars from "react-custom-scrollbars-2";
import RecordsNotFound from "../../components/RecordsNotFound";
import CartItem from "./CartItem";
import {Button, Input, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {addOrder, acknowledgeCartChangelog, editCart, getCoupons, getCustomerShippingAddresses, updateCartShippingAddress} from "../../api/api";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import {useEffect, useRef, useState} from "react";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import Avatar from "react-avatar";
import Loader from "../../components/Loader";
import AddCustomerShippingAddress from "../customers/AddCustomerShippingAddress";
import EditCustomerShippingAddress from "../customers/EditCustomerShippingAddress";


function Cart(props) {
    // const customerEmail = props.customer;
    // console.log("customerEmail", customerEmail)
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [cartDetailsModalOpen, setCartDetailsModalOpen] = useState(false);
    const [shippingAddressModalOpen, setShippingAddressModalOpen] = useState(false);
    const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
    const [changelogDetailsOpen, setChangelogDetailsOpen] = useState(false);
    const [addShippingModalOpen, setAddShippingModalOpen] = useState(false);
    const [editShippingModalOpen, setEditShippingModalOpen] = useState(false);
    const [editingShippingAddress, setEditingShippingAddress] = useState(null);

    const [paymentMethod, setPaymentMethod] = useState(props.defaultPaymentMethod || 'CASH');

    const [cartData, setCartData] = useState(props.data);

    const customerEmail = cartData?.results?.[0]?.customer;

    useEffect(() => {
        if (props.data) {
            setCartData(props.data);

            const appliedCoupon = props.data.coupon;
            if (appliedCoupon) {
                setCouponInputCode(appliedCoupon.code);
                setSelectedCouponId(appliedCoupon.id);
                setSelectedCartId(props.data.id);
            }
        }
    }, [props.data]);

    useEffect(() => {
        if (props.defaultPaymentMethod) {
            setPaymentMethod(props.defaultPaymentMethod);
        }
    }, [props.defaultPaymentMethod]);

    useEffect(() => {
        if (!cartData?.results?.length) return;

        const backendShippingAddress =
            cartData.results[0].shipping_address ?? null;

        setSelectedShippingAddress(backendShippingAddress);
    }, [cartData]);


    const changelog = cartData?.results?.[0]?.cart_changelog || [];
    const hasChangelog = changelog.length > 0;
    const changelogHasPriceChange = changelog.some(e => e.old_price != null || e.new_price != null);

    const acknowledgeChangelogMutation = useMutation(
        () => acknowledgeCartChangelog(cartData?.results?.[0]?.id),
        {
            onSuccess: () => {
                setChangelogDetailsOpen(false);
                // Invalidate the cart query so CartPage re-fetches with cleared changelog
                queryClient.invalidateQueries(['cart', props.customer]);
            },
            onError: () => toast.error('Failed to acknowledge changes. Please try again.'),
        }
    );

    const toggleCartDetailsModal = () => setCartDetailsModalOpen((prevState) => !prevState);
    const toggleShippingAddressModal = () => setShippingAddressModalOpen((prevState) => !prevState);
    const toggleAddShippingModal = () => setAddShippingModalOpen((prevState) => !prevState);

    const placeOrderMutation = useMutation(addOrder, {
        onSuccess: (data) => {
            const shortBillPath = `/sell/orders/${data.id}/short-bill`;
            if (props.autoPrintShortBill) {
                toast.success('Order placed successfully!');
                navigate(shortBillPath, { state: { returnTo: '/sell/cart' } });
                return;
            }
            toast.success(
                <span>Order placed successfully! <u>Print short bill</u></span>,
                { onClick: () => navigate(shortBillPath) }
            );
            navigate('/sell/orders', { state: { orderId: data.order_number } });
        },
        onError: (error) => {
            // DRF field errors are usually a list of strings, but some serializers
            // raise a bare string — read both shapes so the message isn't sliced.
            const firstError = (field) => (Array.isArray(field) ? field[0] : field);

            const customerError = firstError(error.response?.data?.customer);
            const cartItemsError = firstError(error.response?.data?.cart_items);

            const errorMessage = customerError || cartItemsError || 'An unexpected error occurred';
            toast.error(errorMessage);
        },
    });

    const handlePlaceOrder = async () => {
        if (hasChangelog) return;
        const formData = { customer: props.customer };
        if (paymentMethod === 'CASH') {
            formData.payment_method = 'CASH';
        }
        placeOrderMutation.mutate(formData);
    };

    const [couponInputCode, setCouponInputCode] = useState('');
    const [selectedCouponId, setSelectedCouponId] = useState(null);
    const [couponDropdown, setCouponDropdown] = useState(false);
    const [coupons, setCoupons] = useState([]);
    // const [shippingAddress, setShippingAddress] = useState([]);
    const [selectedCartId, setSelectedCartId] = useState(null);
    const couponDropdownRef = useRef(null);
    const page = 1;
    const records = 9;

    // Fetch coupons using useQuery
    const { refetch } = useQuery({
        queryKey: ['coupons'],
        queryFn: () => getCoupons(page, '', '', '', '', '', '', '', '', '', '', '', '', '', '', records),
        retry: false,
        onSuccess: (data) => {
            const applicableCoupons = data.results.filter(coupon =>
                coupon.applicable_to === "CART" || coupon.applicable_to === "BOTH"
            );
            setCoupons(applicableCoupons);
        },
        onError: (err) => {
            handleUnauthorized401QueryError(err, refetch);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        },
    });

    // const { refetch } = useQuery({
    //     queryKey: ['shippingAddress'],
    //     queryFn: () => getShippingAddress(page, props.customer),
    //     retry: false,
    //     onSuccess: (data) => {
    //         const applicableShippingAddress = data.results.filter(coupon =>
    //             coupon.applicable_to === "CART" || coupon.applicable_to === "BOTH"
    //         );
    //         setShippingAddress(applicableShippingAddress);
    //     },
    //     onError: (err) => {
    //         handleUnauthorized401QueryError(err, refetch);
    //         if (err.response.status !== 401) {
    //             toast.error(`An error occurred! ${err?.response?.data?.detail}`);
    //         }
    //     },
    // });

    const {
        data: shippingAddressData,
        isLoading: isShippingAddressLoading,
        refetch: refetchShippingAddress
    } = useQuery({
        queryKey: ['shippingAddress', customerEmail],
        queryFn: () =>
            getCustomerShippingAddresses(page, customerEmail),
        enabled: !!customerEmail,
        retry: false,
        onError: (err) => {
            handleUnauthorized401QueryError(err, refetchShippingAddress);
            if (err?.response?.status !== 401) {
                toast.error(err?.response?.data?.detail || 'Failed to load shipping addresses');
            }
        }
    });


    const filteredCoupons = coupons.filter(coupon =>
        coupon.code.toLowerCase().includes(couponInputCode.toLowerCase())
    );

    const handleInputChange = (event) => {
        setCouponInputCode(event.target.value);
        setCouponDropdown(true);
    };

    const handleInputFocus = () => {
        setCouponDropdown(true);
    };

    const handleSelectCoupon = (couponCode, couponId, cartId) => {
        setCouponInputCode(couponCode);
        setSelectedCouponId(couponId);
        setCouponDropdown(false);
        setSelectedCartId(cartId);
    };

    const handleApplyCoupon = () => {

        const matchingCoupon = coupons.find(coupon => coupon.code.toLowerCase() === couponInputCode.toLowerCase());

        if (!matchingCoupon) {
            if (!toast.isActive('InvalidCoupon')) {
                toast.error('Invalid coupon code', { toastId: 'InvalidCoupon' });
                return;
            }
        }

        if (selectedCartId && matchingCoupon.id) {
            if (selectedCouponId === !matchingCoupon.id) {
                toast.error('This coupon is already applied');
                return;
            }
            const formData = new FormData();
            formData.append('coupon', matchingCoupon.id);
            editCartItemsMutation.mutate({
                formData: formData,
                cartId: selectedCartId,
            });
        }
    };

    const handleRemoveCoupon = (selectedCartId) => {
        const currentCart = cartData.results.find(cart => cart.id === selectedCartId);

        if (currentCart && currentCart.coupon) {
            const formData = new FormData();
            formData.append('coupon', '');
            editCartItemsMutation.mutate({
                formData: formData,
                cartId: selectedCartId
            }, {
                onSuccess: () => {
                    setCouponInputCode('');
                    toast.success("Coupon removed successfully");
                },
                onError: (err) => {
                    toast.error(`An error occurred: ${err.message}`);
                },
            });
        } else {
            toast.error("No coupon to remove");
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (couponDropdownRef.current && !couponDropdownRef.current.contains(event.target)) {
                setCouponDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const editCartItemsMutation = useMutation({
        mutationFn: editCart,
        onSuccess: (data) => {
            if (data.coupon) {
                if (!toast.isActive('couponAppliedToast')) {
                    toast.success(`Coupon Applied`, { toastId: 'couponAppliedToast' });
                }
            }
            const updatedCartData = { ...cartData };
            const updatedResults = updatedCartData.results.map(cart =>
                cart.id === data.id ? data : cart
            );
            setCartData({ ...updatedCartData, results: updatedResults });
        },
        onError: (err) => {
            const cartError = `An error occurred: ${err.message}`;
            const couponError = err?.response?.data?.coupon?.[0];

            const toastId = 'cart-toast-error';

            if (couponError) {
                if (!toast.isActive(toastId)) {
                    toast.error(couponError, { toastId });
                }
            } else {
                if (!toast.isActive(toastId)) {
                    toast.error(cartError, { toastId });
                }
            }
        }
    });

    const updateShippingAddressMutation = useMutation({
        mutationFn: updateCartShippingAddress,
        onSuccess: (data) => {
            // update cartData locally
            const updatedCartData = { ...cartData };
            updatedCartData.results = updatedCartData.results.map(cart =>
                cart.id === data.id ? data : cart
            );

            setCartData(updatedCartData);

            toast.success('Shipping address updated');
            toggleShippingAddressModal();
        },
        onError: (err) => {
            toast.error(
                err?.response?.data?.detail || 'Failed to update shipping address'
            );
        }
    });

    const selectedShippingAddressLabel = (() => {
        const shippingAddressId = cartData?.results?.[0]?.shipping_address;

        if (!shippingAddressId) {
            return 'Not selected';
        }

        const address = shippingAddressData?.results?.find(
            addr => addr.id === shippingAddressId
        );

        return address?.label || 'Not selected';
    })();



    return (
        <>
            <div className="card-box">
                {props.customer && props.status === "success" && cartData?.count > 0 ? cartData?.results?.map(cart => (
                            <div className="table-responsive">
                                <Scrollbars style={{height: '75vh'}}>
                                    <table className="table mb-0">
                                        <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Price</th>
                                            <th className="text-center">Quantity</th>
                                            <th>Total</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {cart?.cart_items?.length > 0 ? cart?.cart_items.map (cartItem => (
                                            <CartItem key={cartItem.id} cartItem={cartItem} />
                                        )) : <RecordsNotFound />}
                                        </tbody>
                                    </table>
                                </Scrollbars>
                                <br/>
                                <div className="cart-coupon d-flex flex-column mb-2 position-relative">
                                    {/*<div className="d-flex">*/}
                                    {/*    <input*/}
                                    {/*        type="text"*/}
                                    {/*        className="form-control form-control-light mr-2"*/}
                                    {/*        placeholder="Apply Cart Coupon"*/}
                                    {/*        aria-label="Recipient's username"*/}
                                    {/*        value={*/}
                                    {/*            cart.coupon*/}
                                    {/*                ? filteredCoupons.find((coupon) => coupon.id === cart.coupon)?.code || ''*/}
                                    {/*                : couponInputCode*/}
                                    {/*        }*/}
                                    {/*        onChange={handleInputChange}*/}
                                    {/*        onFocus={handleInputFocus}*/}
                                    {/*        disabled={cart.coupon}*/}
                                    {/*    />*/}
                                    {/*    {cart.coupon ? (*/}
                                    {/*        <button*/}
                                    {/*            type="button"*/}
                                    {/*            className="input-group-text btn btn-light"*/}
                                    {/*            onClick={() => handleRemoveCoupon(cart.id)}*/}
                                    {/*            disabled={editCartItemsMutation.isLoading}*/}
                                    {/*        >*/}
                                    {/*            {editCartItemsMutation.isLoading? <><Loader size={'small'}/>Removing Coupon...</>: `Remove Coupon`} */}
                                    {/*        </button>*/}
                                    {/*    ) : (*/}
                                    {/*        <button*/}
                                    {/*            type="button"*/}
                                    {/*            className="input-group-text btn btn-primary"*/}
                                    {/*            onClick={handleApplyCoupon}*/}
                                    {/*            disabled={editCartItemsMutation.isLoading}*/}
                                    {/*        >*/}
                                    {/*            {editCartItemsMutation.isLoading? <><Loader size={'small'}/>Apply...</>: `Apply`} */}
                                    {/*        </button>*/}
                                    {/*    )}*/}
                                    {/*</div>*/}

                                    {couponDropdown && !cart.coupon && (
                                        <div
                                            className="dropdown-menu show mt-1"
                                            style={{ position: 'absolute', zIndex: 1000 }}
                                            ref={couponDropdownRef}
                                        >
                                            <Scrollbars style={{height: "20vh"}}>
                                            {filteredCoupons.length > 0 ? (
                                                filteredCoupons.map((coupon, index) => (
                                                    <div
                                                        key={index}
                                                        className="dropdown-item"
                                                        onClick={() => handleSelectCoupon(coupon.code, coupon.id, cart.id)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {coupon.code}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="dropdown-item text-muted">No coupons found</div>
                                            )}
                                            </Scrollbars>
                                        </div>
                                    )}
                                </div>
                                <div className="table-responsive">
                                    <p>Bill Details:</p>
                                    <table
                                        className="table table-centered table-bordered table-sm mb-0"
                                        id="cart-table">
                                        <tbody className="text-center">
                                        {/*<tr>*/}
                                        {/*    <td style={{width: 420}}>Cart Items Sub-Total</td>*/}
                                        {/*    <td style={{width: "40%"}}>*/}
                                        {/*        {cart.coupon ? (*/}
                                        {/*            <>*/}
                                        {/*                <span className="text-muted ml-2">*/}
                                        {/*                    <del>₹ {cart.cart_items_total_before_discount || 0.00}</del>*/}
                                        {/*                </span>*/}
                                        {/*                <span className="ml-2">₹ {cart.cart_items_total_after_discount || 0.00}</span>*/}
                                        {/*            </>*/}
                                        {/*        ) : (*/}
                                        {/*            <span>₹ {cart.cart_items_total_before_discount || 0.00}</span>*/}
                                        {/*        )}*/}
                                        {/*    </td>*/}
                                        {/*</tr>*/}
                                        <tr>
                                            <td style={{ width: 420 }}>Shipping Address</td>
                                            <td>{selectedShippingAddressLabel}</td>
                                        </tr>

                                        <tr>
                                            <td style={{width: 420}}>Sub Total</td>
                                            <td>₹ {cart.cart_items_total_before_discount || 0.00}
                                            </td>
                                        </tr>
                                        {cart.coupon_discount > 0 && (
                                            <>
                                                <tr>
                                                    <td style={{width: 420}}>Discount</td>
                                                    <td>₹ {cart.coupon_discount || 0.00}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{width: 420}}>Sub Total(After Discount)</td>
                                                    <td>₹ {cart.cart_items_total_after_discount || 0.00}
                                                    </td>
                                                </tr>
                                            </>
                                        )}
                                        {cart.cgst > 0 && (
                                            <tr>
                                                <td style={{width: 420}}>CGST</td>
                                                <td>₹ {cart.cgst || 0.00}</td>
                                            </tr>
                                        )}
                                        {cart.sgst > 0 && (
                                            <tr>
                                                <td style={{width: 420}}>SGST</td>
                                                <td>₹ {cart.sgst || 0.00}</td>
                                            </tr>
                                        )}
                                        {cart.igst > 0 && (
                                            <tr>
                                                <td style={{width: 420}}>IGST</td>
                                                <td>₹ {cart.igst || 0.00}</td>
                                            </tr>
                                        )}
                                        {cart.utgst > 0 && (
                                            <tr>
                                                <td style={{width: 420}}>UTGST</td>
                                                <td>₹ {cart.utgst || 0.00}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td style={{width: 420}}>Total Tax Amount</td>
                                            <td>₹ {cart.tax_amount || 0.00}</td>
                                        </tr>
                                        <tr>
                                            <td style={{width: 420}}>Cart Total</td>
                                            <td>₹ {cart.cart_total || 0.00}
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex align-items-center mt-3 mb-1">
                                    <span style={{color: '#adb5bd', fontSize: 13, marginRight: 10}}>Payment Method:</span>
                                    <div
                                        onClick={() => setPaymentMethod(paymentMethod === 'CASH' ? 'RAZORPAY' : 'CASH')}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            background: '#2a3042',
                                            borderRadius: 20,
                                            padding: '3px 4px',
                                            border: '1px solid #3b4256',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <span style={{
                                            padding: '4px 14px',
                                            borderRadius: 16,
                                            fontSize: 13,
                                            fontWeight: 500,
                                            background: paymentMethod === 'CASH' ? '#556ee6' : 'transparent',
                                            color: paymentMethod === 'CASH' ? '#fff' : '#adb5bd',
                                            transition: 'all 0.25s ease',
                                        }}>Cash</span>
                                        <span style={{
                                            padding: '4px 14px',
                                            borderRadius: 16,
                                            fontSize: 13,
                                            fontWeight: 500,
                                            background: paymentMethod === 'RAZORPAY' ? '#556ee6' : 'transparent',
                                            color: paymentMethod === 'RAZORPAY' ? '#fff' : '#adb5bd',
                                            transition: 'all 0.25s ease',
                                        }}>Razorpay</span>
                                    </div>
                                </div>
                                <div className="d-flex order-button">
                                    <div className="form-group mt-3 mt-md-0 mr-2">
                                        <Button
                                            color="primary"
                                            className="waves-effect waves-light"
                                            onClick={toggleShippingAddressModal}
                                        >
                                            Select Shipping Address
                                        </Button>
                                    </div>
                                    <div className="form-group mt-3 mt-md-0 mr-2">
                                        <Button
                                            color="primary"
                                            className="waves-effect waves-light"
                                            onClick={toggleCartDetailsModal}
                                        >
                                            Preview Cart
                                        </Button>
                                    </div>
                                    <div className="form-group mt-3 mt-md-0">
                                        <Button color="btn bx-btn-primary waves-effect waves-light"
                                                onClick={handlePlaceOrder} disabled={placeOrderMutation.isLoading || hasChangelog}>
                                           {placeOrderMutation.isLoading? <><Loader size={'small'}/>Placing Order...</>: `Place Order`}
                                        </Button>
                                    </div>
                                </div>
                                {hasChangelog && (
                                    <div className="mt-3 mb-0" style={{
                                        fontSize: 13,
                                        backgroundColor: 'rgba(247,184,75,0.12)',
                                        border: '1px solid rgba(247,184,75,0.4)',
                                        borderRadius: 6,
                                        padding: '12px 14px',
                                    }}>
                                        <div className="d-flex justify-content-between align-items-start" style={{gap: 12}}>
                                            <div style={{flex: 1}}>
                                                <div style={{color: '#f7b84b', fontWeight: 600, marginBottom: 2}}>
                                                    {changelogHasPriceChange
                                                        ? 'Cart prices have changed'
                                                        : 'Cart tax has been updated'}
                                                </div>
                                                <div className="text-muted" style={{fontSize: 12}}>
                                                    {changelogHasPriceChange
                                                        ? 'Some item prices in this cart were updated. Please review before placing the order.'
                                                        : 'The tax breakdown for this cart has changed due to an address or tax setting update. Please review before placing the order.'}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setChangelogDetailsOpen(v => !v)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#f7b84b',
                                                    fontSize: 12,
                                                    textDecoration: 'underline',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {changelogDetailsOpen ? 'Hide details' : 'View details'}
                                            </button>
                                        </div>
                                        {changelogDetailsOpen && (
                                            <div className="mt-2">
                                                {changelog.map((entry, idx) => (
                                                    <div key={idx} className="rounded p-2 mb-2" style={{backgroundColor: '#fff', border: '1px solid rgba(247,184,75,0.3)'}}>
                                                        {entry.item_name && (
                                                            <div className="fw-semibold" style={{fontSize: 12}}>
                                                                {entry.item_name}
                                                            </div>
                                                        )}
                                                        <div className="text-muted text-capitalize" style={{fontSize: 11}}>
                                                            {entry.reason?.replace(/_/g, ' ')}
                                                        </div>
                                                        {(entry.old_price != null || entry.new_price != null) && (
                                                            <div style={{fontSize: 12}}>
                                                                Price:{' '}
                                                                <span style={{color: '#f1556c'}}>₹{entry.old_price ?? '—'}</span>
                                                                {' → '}
                                                                <span style={{color: '#1abc9c'}}>₹{entry.new_price ?? '—'}</span>
                                                            </div>
                                                        )}
                                                        {(entry.old_state != null || entry.new_state != null) && (
                                                            <div style={{fontSize: 12}}>
                                                                State:{' '}
                                                                <span style={{color: '#f1556c'}}>{entry.old_state ?? '—'}</span>
                                                                {' → '}
                                                                <span style={{color: '#1abc9c'}}>{entry.new_state ?? '—'}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <Button
                                            size="sm"
                                            className="mt-2"
                                            onClick={() => acknowledgeChangelogMutation.mutate()}
                                            disabled={acknowledgeChangelogMutation.isLoading}
                                            style={{backgroundColor: '#f7b84b', borderColor: '#f7b84b', color: '#fff', fontWeight: 500}}
                                        >
                                            {acknowledgeChangelogMutation.isLoading
                                                ? <><Loader size="small" /> Confirming...</>
                                                : 'I understand, continue'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                )): <RecordsNotFound />
                }
            </div>

            <Modal className="modal-full text-center" isOpen={cartDetailsModalOpen} toggle={toggleCartDetailsModal}>
                <ModalHeader toggle={toggleCartDetailsModal} close={
                    <button className="close" onClick={toggleCartDetailsModal}>
                        &times;
                    </button>
                }>Order Items</ModalHeader>
                <ModalBody>
                    <table className="table mb-0" style={{ marginTop: '-17px' }}>
                        <thead>
                        <tr>
                            <th>#</th>
                            <th>Image</th>
                            <th>Item Name</th>
                            <th>Item Type</th>
                            <th>Rate</th>
                            <th>Quantity</th>
                            <th>Price Before Tax(Before Discount)</th>
                            <th>Discount</th>
                            <th>Price Before Tax(After Discount)</th>
                            <th>Tax Percentage</th>
                            <th>Tax Amount</th>
                            <th>Price After Tax</th>
                            {/*<th>Discount</th>*/}
                            {/*<th>Total</th>*/}
                        </tr>
                        </thead>
                        <tbody>
                        {props.data?.results[0]?.cart_items?.map((cart, index) => (
                            <tr key={cart.id}>
                                <th scope="row">{index + 1}</th>
                                <td>
                                    {cart?.product_add_on_item?.product?.image || cart?.product_group_add_on_item?.product_group?.image ? (
                                        <img
                                            src={
                                                cart?.product_add_on_item?.product?.image ||
                                                cart?.product_group_add_on_item?.product_group?.image
                                            }
                                            className="avatar-sm img-thumbnail rounded-circle"
                                            alt="product"
                                        />
                                    ) : (
                                        <Avatar
                                            size={35}
                                            name={
                                                cart?.product_add_on_item?.product?.name ||
                                                cart?.product_group_add_on_item?.product_group?.name
                                            }
                                            round
                                        />
                                    )}
                                </td>
                                <td>
                                    {cart.name}
                                    {cart.product_add_on_item?.add_on_product && cart.product_add_on_item?.add_on_product.map((addOn, addOnIndex) => (
                                        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>
                                            <div className="addon-item" style={{ marginLeft: '5px' }}>
                                                {addOn.name}
                                            </div>
                                        </div>
                                    ))}
                                    {cart.product_group_add_on_item?.add_on_product && cart.product_group_add_on_item?.add_on_product.map((addOn, addOnIndex) => (
                                        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>
                                            <div className="addon-item" style={{ marginLeft: '5px' }}>
                                                {addOn.name}
                                            </div>
                                        </div>
                                    ))}
                                </td>
                                <td>{cart.type}</td>
                                <td>
                                    ₹ {cart.product_add_on_item?.product.price_before_tax ||
                                    cart.product_group_add_on_item?.product_group?.price_before_tax}
                                    {cart.product_add_on_item?.add_on_product && cart.product_add_on_item.add_on_product.map((addOn, addOnIndex) => (
                                        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>
                                            <div className="addon-item" style={{ marginLeft: '5px' }}>
                                                ₹ {addOn.price}
                                            </div>
                                        </div>
                                    ))}
                                    {cart.product_group_add_on_item?.add_on_product && cart.product_group_add_on_item.add_on_product.map((addOn, addOnIndex) => (
                                        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>
                                            <div className="addon-item" style={{ marginLeft: '5px' }}>
                                                ₹ {addOn.price}
                                            </div>
                                        </div>
                                    ))}
                                </td>
                                <td>{cart.quantity}</td>
                                <td>
                                    ₹ {cart.amount_total_before_tax_before_discount}
                                    {/*{cart.product_add_on_item?.add_on_product && cart.product_add_on_item.add_on_product.map((addOn, addOnIndex) => (*/}
                                    {/*    <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                    {/*        <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                    {/*            ₹ {addOn.price_before_tax}*/}
                                    {/*        </div>*/}
                                    {/*    </div>*/}
                                    {/*))}*/}
                                    {/*{cart.product_group_add_on_item?.add_on_product && cart.product_group_add_on_item.add_on_product.map((addOn, addOnIndex) => (*/}
                                    {/*    <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                    {/*        <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                    {/*            ₹ {addOn.price_before_tax}*/}
                                    {/*        </div>*/}
                                    {/*    </div>*/}
                                    {/*))}*/}
                                </td>
                                <td>
                                    {cart.coupon_discount}

                                    {cart.coupon && (
                                        <>
                                            <br />
                                            <span>Applied Coupon: {cart.coupon}</span>
                                        </>
                                    )}
                                </td>
                                <td>{cart.amount_total_before_tax_after_discount}</td>
                                <td>{cart.tax_percentage} %</td>
                                <td>{cart.tax_amount}</td>
                                <td>₹ {cart.amount_total_after_tax}</td>
                                {/*<td>*/}
                                {/*    {cart.product_add_on_item?.product.tax_percentage ||*/}
                                {/*        cart.product_group_add_on_item?.product_group?.tax_percentage.toFixed(2)} %*/}
                                {/*    <span className="text-muted ml-1">*/}
                                {/*        ({cart.product_add_on_item?.product.tax_type === 'TAX-INCLUSIVE' || cart.product_group_add_on_item?.product_group?.tax_type === 'TAX-INCLUSIVE' ? 'incl' : 'excl'})*/}
                                {/*    </span>*/}
                                {/*    {cart.product_add_on_item?.product && cart.product_add_on_item.add_on_product.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                {addOn.tax_percentage} %*/}
                                {/*                <span className="text-muted ml-1">*/}
                                {/*                    ({addOn.tax_type === 'TAX-INCLUSIVE' ? 'incl' : 'excl'})*/}
                                {/*                </span>*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*    {cart.product_group_add_on_item?.add_on_product && cart.product_group_add_on_item.add_on_product.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                {addOn.tax_percentage} %*/}
                                {/*                <span className="text-muted ml-1">*/}
                                {/*                    ({addOn.tax_type === 'TAX-INCLUSIVE' ? 'incl' : 'excl'})*/}
                                {/*                </span>*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*</td>*/}
                                {/*<td>*/}
                                {/*    ₹ {cart.product_add_on_item?.product.tax_amount ||*/}
                                {/*    cart.product_group_add_on_item.product_group?.tax_amount}*/}
                                {/*    {cart.product_add_on_item?.product && cart.product_add_on_item.add_on_product.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                ₹ {addOn.tax_amount}*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*    {cart.product_group_add_on_item?.add_on_product && cart.product_group_add_on_item.add_on_product.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                ₹ {addOn.tax_amount}*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*</td>*/}
                                {/*<td>*/}
                                {/*    ₹ {cart.product_add_on_item?.product.price_after_tax ||*/}
                                {/*    cart.product_group_add_on_item?.product_group?.price_after_tax}*/}
                                {/*    {cart.product_add_on_item?.product && cart.product_add_on_item.add_on_product.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                ₹ {addOn.price_after_tax}*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*    {cart.product_group_add_on_item?.add_on_product && cart.product_group_add_on_item.add_on_product.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                ₹ {addOn.price_after_tax}*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*</td>*/}
                                {/*<td>₹ {cart.coupon_discount === null || cart.coupon_discount === "0.00" ? '0.00' : cart.coupon_discount}</td>*/}
                                {/*<td>₹ {cart?.amount_total_after_tax}</td>*/}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    {/*<div>*/}
                    {/*    /!*<p style={{ height: '60px', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', margin: 0, padding: '10px' }}>*!/*/}
                    {/*    /!*    Cart Items Total:  ₹ {cartData?.results[0]?.cart_total}*!/*/}
                    {/*    /!*</p>*!/*/}
                    {/*    /!*<p style={{ height: '60px', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', margin: 0, padding: '10px' }}>*!/*/}
                    {/*    /!*    Sub Total: ₹ {cartData?.results[0]?.cart_items_total_before_discount} <br/>*!/*/}
                    {/*    /!*    Discount: ₹ {cartData?.results[0]?.coupon_discount} <br/>*!/*/}
                    {/*    /!*    Sub Total(After Discount): ₹ {cartData?.results[0]?.cart_items_total_after_discount} <br/>*!/*/}
                    {/*    /!*    CGST: ₹ {cartData?.results[0]?.cgst} <br/>*!/*/}
                    {/*    /!*    SGST: ₹ {cartData?.results[0]?.sgst} <br/>*!/*/}
                    {/*    /!*    IGST: ₹ {cartData?.results[0]?.igst} <br/>*!/*/}
                    {/*    /!*    UTGST: ₹ {cartData?.results[0]?.utgst} <br/>*!/*/}
                    {/*    /!*    Total Tax Amount: ₹ {cartData?.results[0]?.tax_amount} <br/>*!/*/}
                    {/*    /!*    Cart Total: ₹ {cartData?.results[0]?.cart_total} <br/>*!/*/}
                    {/*    /!*</p>*!/*/}
                    {/*</div>*/}
                </ModalBody>
                {/*<ModalFooter>*/}

                {/*    <p className="text-center">*/}
                {/*        Sub Total: ₹ {cartData?.results[0]?.cart_items_total_before_discount} <br/>*/}
                {/*        Discount: ₹ {cartData?.results[0]?.coupon_discount} <br/>*/}
                {/*        Sub Total(After Discount): ₹ {cartData?.results[0]?.cart_items_total_after_discount} <br/>*/}
                {/*        CGST: ₹ {cartData?.results[0]?.cgst} <br/>*/}
                {/*        SGST: ₹ {cartData?.results[0]?.sgst} <br/>*/}
                {/*        IGST: ₹ {cartData?.results[0]?.igst} <br/>*/}
                {/*        UTGST: ₹ {cartData?.results[0]?.utgst} <br/>*/}
                {/*        Total Tax Amount: ₹ {cartData?.results[0]?.tax_amount} <br/>*/}
                {/*        Cart Total: ₹ {cartData?.results[0]?.cart_total} <br/>*/}
                {/*    </p>*/}
                {/*</ModalFooter>*/}
                <ModalFooter className="d-flex justify-content-end">
                    <div style={{ textAlign: 'right', lineHeight: '1.8' }}>
                        <p className="mb-0">
                            <strong>Sub Total:</strong> ₹ {cartData?.results[0]?.cart_items_total_before_discount || 0.00} <br />
                            {cartData?.results[0]?.coupon_discount > 0 && (
                                <>
                                    <strong>Discount:</strong> ₹ {cartData?.results[0]?.coupon_discount} <br />
                                    <strong>Sub Total (After Discount):</strong> ₹ {cartData?.results[0]?.cart_items_total_after_discount} <br />
                                </>
                            )}
                            {cartData?.results[0]?.cgst > 0 && (
                                <>
                                    <strong>CGST:</strong> ₹ {cartData?.results[0]?.cgst} <br />
                                </>
                            )}
                            {cartData?.results[0]?.sgst > 0 && (
                                <>
                                    <strong>SGST:</strong> ₹ {cartData?.results[0]?.sgst} <br />
                                </>
                            )}
                            {cartData?.results[0]?.utgst > 0 && (
                                <>
                                    <strong>UTGST:</strong> ₹ {cartData?.results[0]?.utgst} <br />
                                </>
                            )}
                            {cartData?.results[0]?.igst > 0 && (
                                <>
                                    <strong>IGST:</strong> ₹ {cartData?.results[0]?.igst} <br />
                                </>
                            )}
                            <strong>Total Tax Amount:</strong> ₹ {cartData?.results[0]?.tax_amount || 0.00} <br />
                            <strong>Cart Total:</strong> ₹ {cartData?.results[0]?.cart_total || 0.00}
                        </p>
                    </div>
                </ModalFooter>

            </Modal>

            {/*<Modal className="modal-lg text-center" isOpen={shippingAddressModalOpen} toggle={toggleShippingAddressModal}>*/}
            {/*    <ModalHeader toggle={toggleShippingAddressModal} close={*/}
            {/*        <button className="close" onClick={toggleShippingAddressModal}>*/}
            {/*            &times;*/}
            {/*        </button>*/}
            {/*    }>Shipping Address</ModalHeader>*/}
            {/*    <ModalBody>*/}
            {/*        <>*/}
            {/*            <div className="row">*/}

            {/*                <div className="col-md-6 mt-1">*/}
            {/*                    <div className="border p-3 rounded mb-3 mb-md-0">*/}
            {/*                        <div className="form-check form-check-inline font-16">*/}
            {/*                            <Input className="form-check-input" type="radio" name="checkoutaddressRadio"*/}
            {/*                                   id="checkoutaddressRadio2"/>*/}
            {/*                            <label className="form-check-label"*/}
            {/*                                   htmlFor="checkoutaddressRadio2">Office</label>*/}
            {/*                        </div>*/}
            {/*                        /!*<div className="float-end">*!/*/}
            {/*                        /!*    <a href="#"><i*!/*/}
            {/*                        /!*        className="mdi mdi-square-edit-outline text-muted font-20"></i></a>*!/*/}
            {/*                        /!*</div>*!/*/}
            {/*                        <h5 className="mt-3">Brent Rowe</h5>*/}

            {/*                        <p className="mb-1"><span className="fw-semibold me-2">Address:</span> 3559*/}
            {/*                            Roosevelt Wilson Lane San Bernardino, CA 92405</p>*/}
            {/*                        <p className="mb-1"><span className="fw-semibold me-2">Phone:</span> (123) 456-7890*/}
            {/*                        </p>*/}
            {/*                        <p className="mb-0"><span className="fw-semibold me-2">Mobile:</span> (+01) 12345*/}
            {/*                            67890</p>*/}
            {/*                    </div>*/}
            {/*                </div>*/}

            {/*            </div>*/}
            {/*        </>*/}
            {/*    </ModalBody>*/}
            {/*    <ModalFooter className="d-flex justify-content-end">*/}
            {/*        <div style={{ textAlign: 'right', lineHeight: '1.8' }}>*/}
            {/*        </div>*/}
            {/*    </ModalFooter>*/}

            {/*</Modal>*/}
            <Modal
                className="modal-lg"
                isOpen={shippingAddressModalOpen}
                toggle={toggleShippingAddressModal}
            >
                <ModalHeader toggle={toggleShippingAddressModal} close={
                    <button className="close" onClick={toggleShippingAddressModal}>
                        &times;
                    </button>
                }>
                    Shipping Address
                </ModalHeader>

                {/*<ModalBody>*/}
                {/*    {isShippingAddressLoading ? (*/}
                {/*        <p>Loading addresses...</p>*/}
                {/*    ) : shippingAddressData?.results?.length ? (*/}
                {/*        <div className="row">*/}
                {/*            {shippingAddressData.results.map((address) => (*/}
                {/*                <div className="col-md-6 mt-2" key={address.id}>*/}
                {/*                    <div className="border p-3 rounded">*/}
                {/*                        <div className="form-check font-16">*/}
                {/*                            <Input*/}
                {/*                                type="radio"*/}
                {/*                                name="shippingAddress"*/}
                {/*                                className="form-check-input"*/}
                {/*                                checked={selectedShippingAddress === address.id}*/}
                {/*                                onChange={() => setSelectedShippingAddress(address.id)}*/}
                {/*                            />*/}
                {/*                            <label className="form-check-label fw-semibold">*/}
                {/*                                {address.label}*/}
                {/*                                {address.is_primary && (*/}
                {/*                                    <span className="badge bg-success ms-2">Primary</span>*/}
                {/*                                )}*/}
                {/*                            </label>*/}
                {/*                        </div>*/}

                {/*                        <p className="mt-2 mb-1">*/}
                {/*                            {address.address_line1}*/}
                {/*                            {address.address_line2 && `, ${address.address_line2}`}*/}
                {/*                            {address.address_line3 && `, ${address.address_line3}`}*/}
                {/*                        </p>*/}

                {/*                        <p className="mb-1">*/}
                {/*                            {address.city}, {address.state?.name} - {address.pincode}*/}
                {/*                        </p>*/}

                {/*                        {address.phone_number && (*/}
                {/*                            <p className="mb-0">*/}
                {/*                                <strong>Phone:</strong> {address.phone_number}*/}
                {/*                            </p>*/}
                {/*                        )}*/}
                {/*                    </div>*/}
                {/*                </div>*/}
                {/*            ))}*/}
                {/*        </div>*/}
                {/*    ) : (*/}
                {/*        <p className="text-muted">No shipping addresses found.</p>*/}
                {/*    )}*/}
                {/*</ModalBody>*/}

                <ModalBody>
                    <Button
                        color="#4d5bed"
                        className="bx-btn-primary mb-2"
                        onClick={toggleAddShippingModal}
                    >
                        + Add New Shipping Address
                    </Button>

                    {isShippingAddressLoading ? (
                        <p>Loading addresses...</p>
                    ) : (
                        <div className="row">
                            {/* No Shipping Address option */}
                            <div className="col-md-6 mt-2">
                                <div className="border p-3 rounded">
                                    <div className="form-check font-16">
                                        <Input
                                            type="radio"
                                            name="shippingAddress"
                                            className="form-check-input"
                                            checked={selectedShippingAddress === null}
                                            onChange={() => setSelectedShippingAddress(null)}
                                        />
                                        <label className="form-check-label fw-semibold">
                                            No Shipping Address
                                        </label>
                                    </div>

                                    <p className="text-muted mt-2 mb-0">
                                        Pickup / delivery not required
                                    </p>
                                </div>
                            </div>

                            {/* Customer shipping addresses */}
                            {shippingAddressData?.results?.length ? (
                                shippingAddressData.results.map((address) => (
                                    <div className="col-md-6 mt-2" key={address.id}>
                                        <div className="border p-3 rounded">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="form-check font-16">
                                                    <Input
                                                        type="radio"
                                                        name="shippingAddress"
                                                        className="form-check-input"
                                                        checked={selectedShippingAddress === address.id}
                                                        onChange={() =>
                                                            setSelectedShippingAddress(address.id)
                                                        }
                                                    />
                                                    <label className="form-check-label fw-semibold">
                                                        {address.label}
                                                        {address.is_primary && (
                                                            <span className="badge bg-success ml-1">
                                                                Primary
                                                            </span>
                                                        )}
                                                    </label>
                                                </div>
                                                <i
                                                    className="mdi mdi-square-edit-outline text-muted font-20 cursor-pointer"
                                                    title="Edit shipping address"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => {
                                                        setEditingShippingAddress(address);
                                                        setEditShippingModalOpen(true);
                                                    }}
                                                />
                                            </div>

                                            <p className="mt-2 mb-1">
                                                {address.address_line1}
                                                {address.address_line2 && `, ${address.address_line2}`}
                                                {address.address_line3 && `, ${address.address_line3}`}
                                            </p>

                                            <p className="mb-1">
                                                {address.city}, {address.state?.name || address.state} - {address.pincode}
                                            </p>

                                            {address.phone_number && (
                                                <p className="mb-0">
                                                    <strong>Phone:</strong> {address.phone_number}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12">
                                    <p className="text-muted mt-2">
                                        No shipping addresses found.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </ModalBody>


                <ModalFooter className="d-flex justify-content-end">
                    {/*<Button*/}
                    {/*    color="primary"*/}
                    {/*    disabled={!selectedShippingAddress}*/}
                    {/*    onClick={() => {*/}
                    {/*        // later: PATCH cart with selectedShippingAddress*/}
                    {/*        toggleShippingAddressModal();*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Use this address*/}
                    {/*</Button>*/}

                    <Button
                        color="primary"
                        disabled={updateShippingAddressMutation.isLoading}
                        onClick={() => {
                            const cartId = cartData?.results?.[0]?.id;

                            updateShippingAddressMutation.mutate({
                                cartId,
                                shippingAddress: selectedShippingAddress
                            });
                        }}
                    >
                        {updateShippingAddressMutation.isLoading
                            ? <><Loader size="small" /> Saving...</>
                            : 'Use this address'}
                    </Button>
                </ModalFooter>
            </Modal>

            <AddCustomerShippingAddress
                isOpen={addShippingModalOpen}
                toggle={() => setAddShippingModalOpen(false)}
                email={customerEmail}
            />

            <EditCustomerShippingAddress
                isOpen={editShippingModalOpen}
                toggle={() => setEditShippingModalOpen(false)}
                email={customerEmail}
                shippingAddress={editingShippingAddress}
            />

        </>
    );
}

export default Cart;
